import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericPost } from '@/_shared/types';
import { getCurrentOrganization } from '@/core/state/selectors';
import { API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_USER_PATH_KEY } from '@/swagger/constants/credits-keys';
import { API_POST_EMPLOYER_CREDITS, KEY_POST_EMPLOYER_CREDITS } from '../constants';
import { MOCK_RESPONSE_POST_EMPLOYER_CREDITS } from '../mocks';
import { ResponsePostEmployerCredits } from '../types';

type FullRequestType = ReturnType<typeof createGeneralCall>['selectors']['request'] & {
  payload: Omit<CallParamsPostEmployerCredits, 'successSaga' | 'errorSaga'>;
};

type RequestPostEmployerCredits = GenericPost<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_USER_PATH_KEY
>['request']['body'];

export type CallParamsPostEmployerCredits = Omit<RequestPostEmployerCredits, 'organizationId'> & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type CallResponsePostEmployerCredits = ResponsePostEmployerCredits;

type CallErrorPostEmployerCredits = {
  message: string;
  code: number;
};

type CallSelectorsPostEmployerCredits = {
  totalAvailableCredits: number;
  monthlyCredits: number;
  consumedCredits: number;
  totalCredits: number;
  trend: {
    creditsConsumed: number;
    creditsConsumedMonthly: number;
    progress: number;
  };
  items: {
    accumulatedCredits: number;
    receivedCredits: number;
    residualCredits: number;
    spentCredits: number;
  }[];
  fullRequest: FullRequestType;
};

let internalPayload = {};

export const postEmployerCreditsCall = createGeneralCall<
  CallParamsPostEmployerCredits,
  CallResponsePostEmployerCredits,
  CallErrorPostEmployerCredits,
  CallSelectorsPostEmployerCredits
>({
  id: KEY_POST_EMPLOYER_CREDITS,
  url: () => state => {
    const org = getCurrentOrganization(state);
    return API_POST_EMPLOYER_CREDITS(org as unknown as string);
  },
  paramsSelector:
    ({ payload }) =>
    () => {
      internalPayload = { ...payload };
      return payload;
    },
  method: 'post',
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_POST_EMPLOYER_CREDITS : undefined,
  selectors: {
    totalAvailableCredits: ({ selectors }) =>
      createSelector(selectors.response, res => res?.totalAvailableCredits || 0),
    monthlyCredits: ({ selectors }) =>
      createSelector(selectors.response, res => res?.monthlyCredits || 0),
    consumedCredits: ({ selectors }) =>
      createSelector(selectors.response, res => res?.consumedCredits || 0),
    totalCredits: ({ selectors }) =>
      createSelector(selectors.response, res => res?.totalCredits || 0),
    trend: ({ selectors }) =>
      createSelector(selectors.response, res => ({
        creditsConsumed: res?.trend?.creditsConsumed || 0,
        creditsConsumedMonthly: res?.trend?.creditsConsumedMonthly || 0,
        progress: res?.trend?.progress || 0,
      })),
    items: ({ selectors }) => createSelector(selectors.response, res => res.items || []),
    fullRequest: ({ selectors }) =>
      createSelector(selectors.request, request => {
        return {
          ...request,
          payload: { ...internalPayload },
        } as unknown as FullRequestType;
      }),
  },
});
